import { useRef, useState } from "react";
import { FileText, Camera, Upload, Plus, Loader2, Trash2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Chip, Field, NSelect, Empty, Loading, useFetch } from "@/components/common";
import { SecureImg, openFile, useUploader, useUploadsPending } from "@/components/Photos";
import { ConfirmDialog } from "@/components/DemoCleanup";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, fDate, todayIso } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const DOC_LEVEL = {
  ok: ["Documents OK", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  bientot: ["Bientôt expiré", "bg-amber-50 text-amber-700 border-amber-200"],
  manquant: ["Document manquant", "bg-slate-100 text-slate-600 border-slate-300"],
  expire: ["Document expiré", "bg-rose-50 text-rose-700 border-rose-200"],
};
export const LIC_LEVEL = {
  valide: ["Valide", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  a_renouveler: ["À renouveler", "bg-amber-50 text-amber-700 border-amber-200"],
  expire: ["Expiré", "bg-rose-50 text-rose-700 border-rose-200"],
  manquant: ["Document manquant", "bg-slate-100 text-slate-600 border-slate-300"],
};
export const DocChip = ({ v, stage, testid = "doc-status-chip" }) => <Chip cls={DOC_LEVEL[v]?.[1]} testid={testid}>{DOC_LEVEL[v]?.[0]}{v === "bientot" && stage ? ` (${stage})` : ""}</Chip>;
export const LicChip = ({ v, stage, testid = "licence-status-chip" }) => <Chip cls={LIC_LEVEL[v]?.[1]} testid={testid}>{LIC_LEVEL[v]?.[0]}{v === "a_renouveler" && stage ? ` (${stage})` : ""}</Chip>;

function FileButtons({ kind, onAdd, testid }) {
  const cam = useRef();
  const file = useRef();
  const [over, setOver] = useState(false);
  const { busy, handle } = useUploader(onAdd, kind);
  const pick = (e) => {
    handle(e.target.files);
    e.target.value = "";
  };
  const drop = (e) => {
    e.preventDefault();
    setOver(false);
    const ok = [...e.dataTransfer.files].filter((x) => x.type === "application/pdf" || x.type.startsWith("image/"));
    if (ok.length < e.dataTransfer.files.length) toast.error("Seuls les PDF et les photos sont acceptés");
    if (ok.length) handle(ok);
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={drop} onClick={() => file.current.click()}
        className={`hidden sm:flex flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-4 py-6 cursor-pointer text-sm transition-colors ${over ? "border-[#F49619] bg-orange-50 text-[#0E2D43]" : "border-slate-300 bg-slate-50 text-slate-500 hover:border-[#0E2D43]"}`}
        data-testid={`${testid}-dropzone`}>
        <Upload className="w-6 h-6" />
        <span className="font-semibold">Glissez-déposez vos documents ici</span>
        <span className="text-xs">ou cliquez pour choisir · PDF ou photos, plusieurs fichiers possibles</span>
      </div>
      <div className="flex flex-wrap gap-2">
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`${testid}-camera-input`} />
      <input ref={file} type="file" multiple accept="application/pdf,image/*" className="hidden" onChange={pick} data-testid={`${testid}-file-input`} />
      <Button type="button" variant="outline" onClick={() => cam.current.click()} data-testid={`${testid}-camera`}><Camera className="w-4 h-4 mr-1" /> Photographier</Button>
      <Button type="button" variant="outline" onClick={() => file.current.click()} data-testid={`${testid}-file`}><Upload className="w-4 h-4 mr-1" /> PDF ou photo</Button>
      {busy > 0 && <Loader2 className="w-5 h-5 animate-spin self-center text-slate-400" />}
      </div>
    </div>
  );
}

export function FileTile({ id, testid, onRemove }) {
  return (
    <div className="relative w-24" data-testid={testid}>
      <button type="button" onClick={() => openFile(id)} className="w-24 h-24 rounded-md border overflow-hidden bg-slate-50 grid place-items-center" title="Ouvrir">
        <SecureImg id={id} className="w-full h-full" alt="Document" fallback={<span className="flex flex-col items-center text-[#0E2D43] text-xs font-bold"><FileText className="w-8 h-8" />PDF</span>} />
      </button>
      {onRemove && <button type="button" onClick={() => onRemove(id)} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-rose-600 text-white text-xs" data-testid={`${testid}-remove`}>×</button>}
    </div>
  );
}

function VehicleDocForm({ vid, types, onSaved }) {
  const [f, setF] = useState({ doc_type: "carte_grise", file_ids: [], valid_until: "", start_date: "", insurer: "", contract_number: "", comment: "" });
  const uploading = useUploadsPending();
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    try {
      await api.post(`/documents/vehicles/${vid}`, { ...f, valid_until: f.valid_until || null, start_date: f.start_date || null });
      toast.success("Document enregistré");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const ins = f.doc_type === "assurance";
  return (
    <div className="border rounded-xl p-4 space-y-3 bg-slate-50/60" data-testid="vehicle-doc-form">
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Type de document"><NSelect value={f.doc_type} onChange={set("doc_type")} testid="doc-type-select" options={Object.entries(types).map(([k, l]) => ({ value: k, label: l }))} /></Field>
        {ins && <Field label="Assureur *"><Input value={f.insurer} onChange={set("insurer")} className="h-10 bg-white" data-testid="doc-insurer-input" /></Field>}
        {ins && <Field label="N° de contrat"><Input value={f.contract_number} onChange={set("contract_number")} className="h-10 bg-white" data-testid="doc-contract-input" /></Field>}
        {ins && <Field label="Date de début"><Input type="date" value={f.start_date} onChange={set("start_date")} className="h-10 bg-white" data-testid="doc-start-input" /></Field>}
        <Field label={ins ? "Date de fin / échéance *" : "Date de validité (si applicable)"}><Input type="date" value={f.valid_until} onChange={set("valid_until")} className="h-10 bg-white" data-testid="doc-valid-input" /></Field>
      </div>
      <FileButtons kind="document" testid="doc-file" onAdd={(id) => setF((x) => ({ ...x, file_ids: [...x.file_ids, id] }))} />
      <div className="flex flex-wrap gap-3">{f.file_ids.map((id, i) => <FileTile key={id} id={id} testid={`doc-new-file-${i}`} onRemove={(x) => setF((y) => ({ ...y, file_ids: y.file_ids.filter((z) => z !== x) }))} />)}</div>
      <Field label="Commentaire"><Textarea rows={2} value={f.comment} onChange={set("comment")} className="bg-white" data-testid="doc-comment-input" /></Field>
      <Button onClick={save} disabled={uploading > 0} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="doc-save-button">{uploading ? "Envoi en cours…" : "Enregistrer le document"}</Button>
    </div>
  );
}

export function VehicleDocuments({ vehicleId, canEdit, canDelete = canEdit }) {
  const { data, loading, reload } = useFetch(`/documents/vehicles/${vehicleId}`);
  const [adding, setAdding] = useState(false);
  const [del, setDel] = useState(null);
  if (loading || !data) return <Loading />;
  const remove = async () => {
    try {
      await api.delete(`/documents/${del.id}`);
      setDel(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="p-4 space-y-4" data-testid="vehicle-documents">
      <div className="flex flex-wrap items-center gap-2">
        <DocChip v={data.summary.status} testid="vehicle-docs-status" />
        {data.required.map((t) => <span key={t} className="text-xs text-slate-500">{data.types[t]} : <DocChip v={data.summary.items[t].level} stage={data.summary.items[t].stage} testid={`vehicle-doc-${t}-status`} /></span>)}
        {canEdit && !adding && <Button onClick={() => setAdding(true)} className="ml-auto bg-[#0E2D43] text-white" data-testid="doc-add-button"><Plus className="w-4 h-4 mr-1" /> Ajouter un document</Button>}
      </div>
      {adding && <VehicleDocForm vid={vehicleId} types={data.types} onSaved={() => { setAdding(false); reload(); }} />}
      {!data.documents.length ? <Empty text="Aucun document enregistré" /> : (
        <div className="space-y-3">{data.documents.map((d) => (
          <div key={d.id} className="border rounded-xl p-3 flex flex-col sm:flex-row gap-3" data-testid={`doc-${d.id}`}>
            <div className="flex gap-2 flex-wrap">{d.file_ids.map((id, i) => <FileTile key={id} id={id} testid={`doc-${d.id}-file-${i}`} />)}</div>
            <div className="text-sm flex-1">
              <div className="flex items-center gap-2 flex-wrap"><FileText className="w-4 h-4 text-[#0E2D43]" /><b className="text-[#0E2D43]">{d.type_label}</b>{d.superseded ? <Chip cls="bg-slate-100 text-slate-500 border-slate-200" testid={`doc-${d.id}-superseded`}>Remplacé (historique)</Chip> : <DocChip v={d.level} stage={d.stage} />}</div>
              {d.insurer && <div>Assureur : <b>{d.insurer}</b>{d.contract_number ? ` · Contrat ${d.contract_number}` : ""}{d.start_date ? ` · du ${fDate(d.start_date)}` : ""}</div>}
              <div className="text-slate-500">{d.valid_until ? `Échéance : ${fDate(d.valid_until)}` : "Sans échéance"} · ajouté le {fDate(d.created_at.slice(0, 10))} par {d.added_by_name}</div>
              {d.comment && <div className="text-slate-500">{d.comment}</div>}
            </div>
            {canDelete && <button onClick={() => setDel(d)} className="self-start p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Retirer" data-testid={`doc-${d.id}-delete`}><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}</div>
      )}
      {del && <ConfirmDialog title="Retirer ce document ?" onConfirm={remove} onClose={() => setDel(null)} label="Retirer" testid="doc-delete-dialog"><p>{del.type_label} — il ne sera plus pris en compte dans les alertes.</p></ConfirmDialog>}
    </div>
  );
}

function LicenceSide({ k, label, value, onSet }) {
  const { busy, handle } = useUploader((id) => onSet([id]), "personal");
  const cam = useRef();
  const fil = useRef();
  const pick = (e) => {
    handle(e.target.files);
    e.target.value = "";
  };
  return (
    <div className="border-2 border-dashed rounded-xl p-3 text-center" data-testid={`licence-${k}`}>
      <div className="text-xs font-bold text-[#0E2D43] mb-2">{label}</div>
      {value[0] ? <div className="flex justify-center"><FileTile id={value[0]} testid={`licence-${k}-file`} onRemove={() => onSet([])} /></div> : busy ? <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /> : null}
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`licence-${k}-camera-input`} />
      <input ref={fil} type="file" accept="application/pdf,image/*" className="hidden" onChange={pick} data-testid={`licence-${k}-file-input`} />
      <div className="flex gap-2 justify-center mt-2">
        <Button type="button" size="sm" variant="outline" onClick={() => cam.current.click()} data-testid={`licence-${k}-camera`}><Camera className="w-4 h-4" /></Button>
        <Button type="button" size="sm" variant="outline" onClick={() => fil.current.click()} data-testid={`licence-${k}-upload`}><Upload className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function LicenceForm({ ownerType, ownerId, onSaved, prev }) {
  const [f, setF] = useState({ recto: [], verso: [], categories: (prev?.categories || ["B"]).join(", "), number: "", obtained_date: prev?.obtained_date || "", valid_until: "", checked_date: todayIso(), comment: "" });
  const uploading = useUploadsPending();
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const save = async () => {
    try {
      await api.post("/documents/licence", {
        owner_type: ownerType, owner_id: ownerId, recto_file_id: f.recto[0] || null, verso_file_id: f.verso[0] || null,
        categories: f.categories.split(/[,\s]+/).filter(Boolean), number: f.number || null, obtained_date: f.obtained_date || null,
        valid_until: f.valid_until || null, checked_date: f.checked_date || null, comment: f.comment,
      });
      toast.success("Permis enregistré");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="border rounded-xl p-4 space-y-3 bg-slate-50/60" data-testid="licence-form">
      <div className="grid grid-cols-2 gap-3">
        <LicenceSide k="recto" label="Recto *" value={f.recto} onSet={(v) => setF((x) => ({ ...x, recto: v }))} />
        <LicenceSide k="verso" label="Verso" value={f.verso} onSet={(v) => setF((x) => ({ ...x, verso: v }))} />
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Catégorie(s)"><Input value={f.categories} onChange={set("categories")} className="h-10 bg-white" placeholder="B, C1…" data-testid="licence-categories-input" /></Field>
        <Field label="N° de permis (facultatif)"><Input value={f.number} onChange={set("number")} className="h-10 bg-white" data-testid="licence-number-input" /></Field>
        <Field label="Date d'obtention"><Input type="date" value={f.obtained_date} onChange={set("obtained_date")} className="h-10 bg-white" data-testid="licence-obtained-input" /></Field>
        <Field label="Validité / expiration"><Input type="date" value={f.valid_until} onChange={set("valid_until")} className="h-10 bg-white" data-testid="licence-valid-input" /></Field>
        <Field label="Contrôlé par Prest'hyg le"><Input type="date" value={f.checked_date} onChange={set("checked_date")} className="h-10 bg-white" data-testid="licence-checked-input" /></Field>
      </div>
      <Field label="Commentaire administratif"><Textarea rows={2} value={f.comment} onChange={set("comment")} className="bg-white" data-testid="licence-comment-input" /></Field>
      <Button onClick={save} disabled={uploading > 0} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid="licence-save-button">{uploading ? "Envoi en cours…" : "Enregistrer le permis"}</Button>
    </div>
  );
}

export function LicenceView({ d, status, showNumber = true }) {
  return (
    <div className="flex flex-col sm:flex-row gap-4" data-testid="licence-view">
      <div className="flex gap-2">{[d.recto_file_id, d.verso_file_id].filter(Boolean).map((id, i) => <FileTile key={id} id={id} testid={`licence-view-file-${i}`} />)}</div>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm flex-1">
        <dt className="text-slate-500">Statut</dt><dd><LicChip v={status.status} stage={status.stage} /></dd>
        <dt className="text-slate-500">Catégorie(s)</dt><dd className="font-semibold">{d.categories.join(", ") || "—"}</dd>
        {showNumber && <><dt className="text-slate-500">N° de permis</dt><dd data-testid="licence-number">{d.number || "—"}</dd></>}
        <dt className="text-slate-500">Obtenu le</dt><dd>{fDate(d.obtained_date)}</dd>
        <dt className="text-slate-500">Validité</dt><dd>{d.valid_until ? fDate(d.valid_until) : "Non renseignée"}</dd>
        <dt className="text-slate-500">Contrôlé le</dt><dd>{d.checked_date ? `${fDate(d.checked_date)} par ${d.checked_by_name || "—"}` : "Non contrôlé"}</dd>
        {d.comment && <><dt className="text-slate-500">Commentaire</dt><dd>{d.comment}</dd></>}
      </dl>
    </div>
  );
}

export function LicencePanel({ ownerType, ownerId }) {
  const { data, loading, reload } = useFetch(`/documents/licence/${ownerType}/${ownerId}`);
  const [adding, setAdding] = useState(false);
  const ro = useAuth().user.real_role === "direction";
  if (loading || !data) return <Loading />;
  return (
    <div className="p-4 space-y-4" data-testid="licence-panel">
      <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="w-4 h-4 text-emerald-600" /> Document personnel — visible uniquement par la Direction, le service administratif et le salarié concerné.</div>
      {data.current ? <LicenceView d={data.current} status={data.status} /> : <Empty text="Aucun permis enregistré (Document manquant)" />}
      {ro ? null : adding ? <LicenceForm ownerType={ownerType} ownerId={ownerId} prev={data.current} onSaved={() => { setAdding(false); reload(); }} /> : (
        <Button onClick={() => setAdding(true)} className="bg-[#0E2D43] text-white" data-testid="licence-add-button"><Plus className="w-4 h-4 mr-1" /> {data.current ? "Mettre à jour le permis" : "Enregistrer le permis"}</Button>
      )}
      {data.history.length > 0 && <div className="text-xs text-slate-500">{data.history.length} version(s) précédente(s) conservée(s).</div>}
    </div>
  );
}
